import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Legend,
  Line,
} from 'recharts';
import { useHistortyData } from '../TransactionHistory/useHistortyData';
import {
  formatToShortDateStrToDate,
  getToken,
  sortedAsc,
} from '../utils';
import Filter from '../Components/Filter';
import { useSearchParams } from 'react-router-dom';
import CustomTooltip from '../Components/CustomTooltip';

export const Graph = () => {
  const { token: userToken } = getToken();
  const userId = getToken()?.user?.id;
  const { transactionHistory } = useHistortyData();
  const [searchParams] = useSearchParams();

  const currencyId = !searchParams.get('currency_id')
    ? 1
    : Number(searchParams.get('currency_id'));

  const [, setIsLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/dashboard/all-settings`;
      try {
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${userToken}` },
        });

        setCurrencies(response.data.currencies);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userToken]);

  const selectedCurrency = currencies.find(
    (currency) => currency.id === currencyId
  );

  let historyDataArray = [];

  if (selectedCurrency) {
    historyDataArray = transactionHistory
      .filter((transaction) => {
        const isCompleted =
          transaction.transaction_status.name === 'Completed';

        const isBuyTransaction =
          (transaction.originator.id === userId &&
            transaction.transaction_type.name === 'Buy' &&
            isCompleted) ||
          (transaction.originator.id !== userId &&
            transaction.transaction_type.name === 'Sell' &&
            isCompleted);

        const isSellTransaction =
          (transaction.originator.id === userId &&
            transaction.transaction_type.name === 'Sell' &&
            isCompleted) ||
          (transaction.originator.id !== userId &&
            transaction.transaction_type.name === 'Buy' &&
            isCompleted);

        return isBuyTransaction || isSellTransaction;
      })
      .sort(sortedAsc)
      .map((transaction) => {
        const amount =
          selectedCurrency.type.id === 1
            ? transaction.payment.total
            : selectedCurrency.type.id === 2
            ? transaction.payment.amount
            : 0;

        return {
          date: formatToShortDateStrToDate(transaction.created_at),
          BUY:
            transaction.base_currency.id === currencyId
              ? transaction.transaction_type.name === 'Buy'
                ? Number(amount)
                : 0
              : transaction.transaction_type.name === 'Buy'
              ? Number(amount)
              : 0,
          SELL:
            transaction.base_currency.id === currencyId
              ? transaction.transaction_type.name === 'Sell'
                ? Number(amount)
                : 0
              : transaction.transaction_type.name === 'Sell'
              ? Number(amount)
              : 0,
          total: transaction.payment.amount,
          currencySymbol: transaction.quote_currency.symbol,
          rateCurrencySymbol: transaction.base_currency.symbol,
          rate: transaction.rate,
          remark: transaction.remark,
        };
      });
  }

  const filterOptions = currencies.map((currency) => ({
    value: currency.id,
    label: currency.symbol,
  }));

  // Custom formatter function for Y-axis tick values
  const yAxisTickFormatter = (value) => `${(value / 1000).toFixed(1)}K`;

  return (
    <>
      <div className="w-full h-full flex flex-col gap-4 border-2 rounded-md pt-1 px-2 overflow-hidden">
        {!!currencies.length && (
          <div className="">
            <Filter filterField="currency_id" options={filterOptions} />
          </div>
        )}
        <div className="w-full h-full overflow-x-auto">
          <ResponsiveContainer
            height="100%"
            width={1200}
            className="overflow-hidden"
          >
            <LineChart data={historyDataArray}
            margin={{ top: 5, right: 30, left: 50, bottom: 5 }} // Adjust left margin
            >
              <CartesianGrid
                opacity={0.4}
                vertical={false}
                strokeDasharray="3 3"
              />
              <XAxis dataKey="date" />
              <YAxis tickFormatter={yAxisTickFormatter} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line type="monotone" dataKey="BUY" stroke="#5AC8FA" />
              <Line type="monotone" dataKey="SELL" stroke="#ff1414" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};
